import { normalizeAppHostedUploadUrl } from './normalizeAppHostedUploadUrl';
import {
  fileTooLargeMessage,
  isFileWithinMegabyteLimit,
  parseLessonPreviewImageUrls,
  parseServerImageUrls,
  resolveLessonPreviewImageSrc,
  validateImageDimensions,
  type ResolvedMediaUploadLimits,
} from './mediaUploadLimits';
import { isEmbeddedMediaUrl, uploadAdminMediaFile } from './adminMediaUpload';
import type { AdminBlockAudioMediaSettings, AdminEditorBlockFields } from './adminEditorBlockTypes';

export type ImageBlockPreviewItem =
  | { kind: 'server'; url: string; previewSrc: string }
  | { kind: 'pending'; file: File; previewSrc: string; pendingIndex: number };

export type ImageDisplayMode = 'stacked' | 'carousel';

export interface AdminBlockImagePendingRefs {
  files: Map<string, File[]>;
  previewUrls: Map<string, string[]>;
  removedServerUrls: Map<string, Set<string>>;
  order: Map<string, string[]>;
  draftItems: Map<string, ImageBlockPreviewItem[]>;
  displayModes: Map<string, ImageDisplayMode>;
}

export function createAdminBlockImagePendingRefs(): AdminBlockImagePendingRefs {
  return {
    files: new Map(),
    previewUrls: new Map(),
    removedServerUrls: new Map(),
    order: new Map(),
    draftItems: new Map(),
    displayModes: new Map(),
  };
}

export function imageBlockItemKey(item: ImageBlockPreviewItem): string {
  return item.kind === 'server' ? `s:${item.url}` : `p:${item.previewSrc}`;
}

export function formatImageUrlList(urls: string[]): string {
  if (urls.length === 0) return '';
  if (urls.length === 1) return urls[0] ?? '';
  return JSON.stringify(urls);
}

function getRemovedServerImageUrls(refs: AdminBlockImagePendingRefs, blockKey: string): Set<string> {
  return refs.removedServerUrls.get(blockKey) ?? new Set<string>();
}

function getActiveServerImageUrls(
  block: Pick<AdminEditorBlockFields, 'imageUrl'>,
  blockKey: string,
  refs: AdminBlockImagePendingRefs
): string[] {
  const removed = getRemovedServerImageUrls(refs, blockKey);
  return parseServerImageUrls(block.imageUrl).filter((url) => !removed.has(url));
}

function buildImageBlockPreviewItemsUnordered(
  blockKey: string,
  block: Pick<AdminEditorBlockFields, 'imageUrl'>,
  refs: AdminBlockImagePendingRefs
): ImageBlockPreviewItem[] {
  const items: ImageBlockPreviewItem[] = [];
  for (const url of getActiveServerImageUrls(block, blockKey, refs)) {
    items.push({
      kind: 'server',
      url,
      previewSrc: normalizeAppHostedUploadUrl(url),
    });
  }

  const pendingPreviews = refs.previewUrls.get(blockKey) ?? [];
  const pendingFiles = refs.files.get(blockKey) ?? [];
  for (let i = 0; i < pendingPreviews.length; i++) {
    const previewSrc = pendingPreviews[i];
    if (!previewSrc) continue;
    items.push({
      kind: 'pending',
      file: pendingFiles[i] ?? new File([], `pending-${i}.jpg`),
      previewSrc,
      pendingIndex: i,
    });
  }

  return items;
}

function syncImageBlockDraftFromSources(
  blockKey: string,
  block: Pick<AdminEditorBlockFields, 'imageUrl'>,
  refs: AdminBlockImagePendingRefs
): ImageBlockPreviewItem[] {
  const fresh = buildImageBlockPreviewItemsUnordered(blockKey, block, refs);
  if (fresh.length === 0) {
    refs.draftItems.delete(blockKey);
    refs.order.delete(blockKey);
    return [];
  }

  const freshByKey = new Map(fresh.map((item) => [imageBlockItemKey(item), item]));
  let draft = refs.draftItems.get(blockKey);

  if (!draft?.length) {
    draft = fresh;
  } else {
    draft = draft
      .filter((item) => freshByKey.has(imageBlockItemKey(item)))
      .map((item) => freshByKey.get(imageBlockItemKey(item))!);
    const draftKeys = new Set(draft.map((item) => imageBlockItemKey(item)));
    for (const item of fresh) {
      if (!draftKeys.has(imageBlockItemKey(item))) {
        draft.push(item);
      }
    }
  }

  refs.draftItems.set(blockKey, draft);
  refs.order.set(
    blockKey,
    draft.map((item) => imageBlockItemKey(item))
  );
  return draft;
}

export function getImageBlockPreviewItems(
  blockKey: string,
  block: Pick<AdminEditorBlockFields, 'imageUrl'>,
  refs: AdminBlockImagePendingRefs
): ImageBlockPreviewItem[] {
  return syncImageBlockDraftFromSources(blockKey, block, refs);
}

/** Resolve image URLs for admin block preview (server URLs + pending blob uploads). */
export function resolveAdminBlockPreviewImageUrls(
  block: Pick<AdminEditorBlockFields, 'type' | 'imageUrl'>,
  blockKey: string | undefined,
  imageRefs: AdminBlockImagePendingRefs | undefined
): string[] {
  if (block.type !== 'image') return [];

  if (imageRefs && blockKey) {
    const items = getImageBlockPreviewItems(blockKey, block, imageRefs);
    if (items.length > 0) {
      return items
        .map((item) => resolveLessonPreviewImageSrc(item.previewSrc))
        .filter((url) => url.length > 0);
    }
  }

  return parseLessonPreviewImageUrls(block.imageUrl)
    .map((url) => resolveLessonPreviewImageSrc(url))
    .filter((url) => url.length > 0);
}

function applyImageBlockItemsToRefs(
  blockKey: string,
  items: ImageBlockPreviewItem[],
  block: Pick<AdminEditorBlockFields, 'imageUrl'>,
  refs: AdminBlockImagePendingRefs
): string {
  const pendingFiles: File[] = [];
  const pendingPreviews: string[] = [];
  const serverUrlsInOrder: string[] = [];

  for (const item of items) {
    if (item.kind === 'server') {
      serverUrlsInOrder.push(item.url);
    } else {
      pendingFiles.push(item.file);
      pendingPreviews.push(item.previewSrc);
    }
  }

  const allOriginalServer = parseServerImageUrls(block.imageUrl);
  const removed = new Set(allOriginalServer.filter((url) => !serverUrlsInOrder.includes(url)));
  if (removed.size > 0) {
    refs.removedServerUrls.set(blockKey, removed);
  } else {
    refs.removedServerUrls.delete(blockKey);
  }

  if (pendingFiles.length > 0) {
    refs.files.set(blockKey, pendingFiles);
    refs.previewUrls.set(blockKey, pendingPreviews);
  } else {
    refs.files.delete(blockKey);
    refs.previewUrls.delete(blockKey);
  }

  refs.order.set(
    blockKey,
    items.map((item) => imageBlockItemKey(item))
  );

  return formatImageUrlList(serverUrlsInOrder);
}

export function commitImageBlockDraftItems(
  blockKey: string,
  items: ImageBlockPreviewItem[],
  block: Pick<AdminEditorBlockFields, 'imageUrl'>,
  refs: AdminBlockImagePendingRefs
): string {
  refs.draftItems.set(blockKey, items);
  refs.order.set(
    blockKey,
    items.map((item) => imageBlockItemKey(item))
  );
  return applyImageBlockItemsToRefs(blockKey, items, block, refs);
}

export function buildFinalImageUrlListFromOrderedItems(
  items: ImageBlockPreviewItem[],
  uploadedPendingUrls: string[]
): string {
  let pendingIdx = 0;
  const urls: string[] = [];
  for (const item of items) {
    if (item.kind === 'server') {
      urls.push(item.url);
    } else {
      const uploaded = uploadedPendingUrls[pendingIdx++];
      if (uploaded) urls.push(uploaded);
    }
  }
  return formatImageUrlList(urls);
}

export function clearImageBlockDraftAtKey(blockKey: string, refs: AdminBlockImagePendingRefs): void {
  const previews = refs.previewUrls.get(blockKey) ?? [];
  for (const preview of previews) {
    if (preview.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }
  }
  refs.files.delete(blockKey);
  refs.previewUrls.delete(blockKey);
  refs.removedServerUrls.delete(blockKey);
  refs.order.delete(blockKey);
  refs.draftItems.delete(blockKey);
}

export function readImageDisplayModeFromBlock(
  block: { transcript?: string },
  blockKey: string,
  refs: AdminBlockImagePendingRefs
): ImageDisplayMode {
  const cached = refs.displayModes.get(blockKey);
  if (cached) return cached;
  return readImageDisplayModeFromPersistedBlock(block);
}

export function readImageDisplayModeFromPersistedBlock(block: { transcript?: string }): ImageDisplayMode {
  if (block.transcript?.startsWith('{')) {
    try {
      const metadata = JSON.parse(block.transcript) as { imageDisplayMode?: ImageDisplayMode };
      if (metadata.imageDisplayMode === 'carousel' || metadata.imageDisplayMode === 'stacked') {
        return metadata.imageDisplayMode;
      }
    } catch {
      /* ignore */
    }
  }
  return 'stacked';
}

/**
 * When a block gains multiple images, seed display mode from persisted metadata (or session cache)
 * instead of resetting to stacked.
 */
export function seedImageDisplayModeWhenMultiImageAdded<K>(
  displayModes: Map<K, ImageDisplayMode>,
  key: K,
  block: { transcript?: string },
  totalImages: number
): void {
  if (totalImages <= 1 || displayModes.has(key)) return;
  displayModes.set(key, readImageDisplayModeFromPersistedBlock(block));
}

export function writeImageDisplayModeToBlock(
  block: AdminEditorBlockFields,
  mode: ImageDisplayMode
): AdminEditorBlockFields {
  let metadata: Record<string, unknown> = {};
  if (block.transcript?.startsWith('{')) {
    try {
      metadata = JSON.parse(block.transcript) as Record<string, unknown>;
    } catch {
      metadata = {};
    }
  }
  metadata.imageDisplayMode = mode;
  return { ...block, transcript: JSON.stringify(metadata) };
}

export async function validateImageFilesForUpload(
  files: FileList | File[],
  mediaSettings: AdminBlockAudioMediaSettings
): Promise<{ validFiles: File[]; totalSize: number }> {
  const maxSizeMB = mediaSettings.maxImageSizeMB || 10;
  const allowedFormats = mediaSettings.allowedImageFormats || ['jpg', 'jpeg', 'png', 'gif', 'webp'];
  const maxWidth = mediaSettings.maxImageWidth || 4096;
  const maxHeight = mediaSettings.maxImageHeight || 4096;

  const validFiles: File[] = [];
  let totalSize = 0;
  const fileArray = Array.from(files);

  for (const file of fileArray) {
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
    if (!allowedFormats.includes(fileExtension)) {
      throw new Error(`Image "${file.name}" format not allowed. Allowed formats: ${allowedFormats.join(', ')}`);
    }
    if (!isFileWithinMegabyteLimit(file.size, maxSizeMB)) {
      throw new Error(fileTooLargeMessage(file.size, maxSizeMB, `Image "${file.name}"`));
    }
    totalSize += file.size;
    validFiles.push(file);
  }

  if (validFiles.length === 0) {
    return { validFiles, totalSize: 0 };
  }

  await Promise.all(validFiles.map((file) => validateImageDimensions(file, maxWidth, maxHeight)));
  return { validFiles, totalSize };
}

export async function prepareAdminImageBlockForSave<T extends AdminEditorBlockFields>(
  block: T,
  blockKey: string,
  refs: AdminBlockImagePendingRefs,
  uploadLimits?: ResolvedMediaUploadLimits
): Promise<T> {
  if (block.type !== 'image') return block;

  const orderedItems = getImageBlockPreviewItems(blockKey, block, refs);
  const pendingFiles = refs.files.get(blockKey) ?? [];
  const uploadedUrls: string[] = [];

  for (const file of pendingFiles) {
    const serverUrl = await uploadAdminMediaFile(file, file.name, 'images', uploadLimits);
    uploadedUrls.push(serverUrl);
  }

  const imageUrl =
    orderedItems.length > 0
      ? buildFinalImageUrlListFromOrderedItems(orderedItems, uploadedUrls)
      : isEmbeddedMediaUrl(block.imageUrl)
        ? ''
        : block.imageUrl ?? '';

  if (uploadedUrls.length > 0 || refs.removedServerUrls.has(blockKey)) {
    clearImageBlockDraftAtKey(blockKey, refs);
  }

  return { ...block, imageUrl };
}

export function isEmbeddedImageUrl(url?: string | null): boolean {
  return !!url && (url.startsWith('data:') || url.startsWith('blob:'));
}
